import { useParams, useNavigate } from 'react-router-dom';
import { useEffect, useState } from 'react';
import DOMPurify from 'dompurify';
import './BlogDetailPage.css';
import Header from '../components/Header';
import Footer from '../components/Footer';

const BLOG_API = '/api/blog.php';

function formatDate(str) {
  if (!str) return '';
  return new Date(str).toLocaleDateString('en-IN', { year: 'numeric', month: 'long', day: 'numeric' });
}

function injectSEO(post) {
  const title    = post.meta_title || post.title;
  const desc     = post.meta_description || post.excerpt || '';
  const img      = post.og_image || post.cover_image || '';
  const canonical = post.canonical_url || '';

  document.title = title ? `${title} | Mad Marketing Club` : 'Mad Marketing Club';

  const setMeta = (sel, attr, val) => {
    let el = document.querySelector(sel);
    if (!el) {
      el = document.createElement('meta');
      if (attr === 'name')     el.setAttribute('name',     sel.match(/name="([^"]+)"/)?.[1]     || '');
      if (attr === 'property') el.setAttribute('property', sel.match(/property="([^"]+)"/)?.[1] || '');
      document.head.appendChild(el);
    }
    el.setAttribute('content', val);
  };

  if (desc) {
    setMeta('meta[name="description"]',         'name',     desc);
    setMeta('meta[property="og:description"]',  'property', desc);
    setMeta('meta[name="twitter:description"]', 'name',     desc);
  }
  if (title) {
    setMeta('meta[property="og:title"]',  'property', `${title} | Mad Marketing Club`);
    setMeta('meta[name="twitter:title"]', 'name',     `${title} | Mad Marketing Club`);
  }
  if (img) {
    setMeta('meta[property="og:image"]',  'property', img);
    setMeta('meta[name="twitter:image"]', 'name',     img);
  }
  if (post.meta_keywords) {
    setMeta('meta[name="keywords"]', 'name', post.meta_keywords);
  }

  let canonEl = document.querySelector('link[rel="canonical"]');
  if (!canonEl) {
    canonEl = document.createElement('link');
    canonEl.setAttribute('rel', 'canonical');
    document.head.appendChild(canonEl);
  }
  canonEl.setAttribute('href', canonical || window.location.href);

  let ogType = document.querySelector('meta[property="og:type"]');
  if (!ogType) {
    ogType = document.createElement('meta');
    ogType.setAttribute('property', 'og:type');
    document.head.appendChild(ogType);
  }
  ogType.setAttribute('content', 'article');
}

export default function BlogDetailPage() {
  const { slug }  = useParams();
  const navigate  = useNavigate();
  const [post,     setPost]     = useState(null);
  const [loading,  setLoading]  = useState(true);
  const [allPosts, setAllPosts] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    setLoading(true);
    setPost(null);

    fetch(`${BLOG_API}?action=get&slug=${encodeURIComponent(slug)}`)
      .then(r => r.json())
      .then(d => {
        if (d.success && d.post) {
          setPost(d.post);
          injectSEO(d.post);
        } else {
          setPost(null);
        }
      })
      .catch(() => setPost(null))
      .finally(() => setLoading(false));

    fetch(`${BLOG_API}?action=list`)
      .then(r => r.json())
      .then(d => { if (d.success) setAllPosts(d.rows || []); })
      .catch(() => {});
  }, [slug]);

  if (loading) {
    return (
      <>
        <Header />
        <div className="bdp-not-found" style={{ minHeight: '60vh' }}>
          <p style={{ color: '#94a3b8', fontSize: 16 }}>Loading…</p>
        </div>
        <Footer />
      </>
    );
  }

  if (!post) {
    return (
      <>
        <Header />
        <div className="bdp-not-found">
          <h1>Post not found</h1>
          <button onClick={() => navigate('/blog')}>Back to Blog</button>
        </div>
        <Footer />
      </>
    );
  }

  const categoryColor = post.category_color || '#4f46e5';

  const related = allPosts.filter(b => b.slug !== slug && b.category === post.category).slice(0, 2);
  const others  = related.length < 2
    ? [...related, ...allPosts.filter(b => b.slug !== slug && b.category !== post.category)].slice(0, 2)
    : related;

  return (
    <>
      <Header />
      <main className="bdp-main">

        {/* Back */}
        <div className="bdp-back-wrap">
          <button className="bdp-back" onClick={() => navigate('/blog')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none"
              stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M19 12H5M12 19l-7-7 7-7" />
            </svg>
            All Articles
          </button>
        </div>

        {/* Hero */}
        <div className="bdp-hero">
          <div className="bdp-hero-inner">
            {post.category && (
              <span className="bdp-cat" style={{ background: categoryColor }}>{post.category}</span>
            )}
            <h1 className="bdp-title">{post.title}</h1>
            <div className="bdp-meta">
              <span>{formatDate(post.published_at)}</span>
              {post.read_time && <><span className="bdp-meta-dot" /><span>{post.read_time}</span></>}
              {post.author    && <><span className="bdp-meta-dot" /><span>{post.author}</span></>}
            </div>
          </div>
          {post.cover_image && (
            <div className="bdp-cover-wrap">
              <img src={post.cover_image} alt={post.title} className="bdp-cover" />
            </div>
          )}
        </div>

        {/* Body */}
        <div className="bdp-layout">
          <article className="bdp-article">
            {post.excerpt && <p className="bdp-lead">{post.excerpt}</p>}

            <div
              className="bdp-body bdp-body--html"
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(post.body || '', { USE_PROFILES: { html: true } }) }}
            />

            {/* CTA */}
            <div className="bdp-cta-box">
              <div className="bdp-cta-text">
                <h3 className="bdp-cta-title">Want results like this for your brand?</h3>
                <p className="bdp-cta-desc">
                  Mad Marketing Club works with ambitious e-commerce brands to build strategies that actually scale. Let's talk.
                </p>
              </div>
              <a href="/#contact" className="bdp-cta-btn">
                Get in Touch
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none"
                  stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M5 12h14M12 5l7 7-7 7" />
                </svg>
              </a>
            </div>
          </article>

          {/* Sidebar */}
          <aside className="bdp-sidebar">
            <div className="bdp-sidebar-card">
              <h4 className="bdp-sidebar-title">About Mad Marketing Club</h4>
              <p className="bdp-sidebar-text">
                Pune-based e-commerce growth partners helping brands scale on Amazon, Shopify, and beyond.
              </p>
              <a href="/#contact" className="bdp-sidebar-btn">Work With Us</a>
            </div>

            {others.length > 0 && (
              <div className="bdp-sidebar-card">
                <h4 className="bdp-sidebar-title">More Articles</h4>
                <div className="bdp-related-list">
                  {others.map(r => (
                    <div
                      key={r.slug}
                      className="bdp-related-item"
                      onClick={() => navigate(`/blog/${r.slug}`)}
                    >
                      {r.cover_image && (
                        <img src={r.cover_image} alt={r.title} className="bdp-related-img" />
                      )}
                      <div className="bdp-related-text">
                        <span className="bdp-related-cat" style={{ color: r.category_color || '#4f46e5' }}>
                          {r.category}
                        </span>
                        <p className="bdp-related-title">{r.title}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </aside>
        </div>
      </main>
      <Footer />
    </>
  );
}
